/** API contract types for the self-service portal user-management proxy. */

export type PortalUserSubscriptionTier =
  | 'free'
  | 'basic'
  | 'pro'
  | 'media_partner'
  | 'internal';

export type PortalUserStatus = 'active' | 'blocked';

export interface PortalSubscriptionUsage {
  gen_edit_used: number;
  gen_edit_limit: number | null;
  resize_used: number;
  resize_limit: number | null;
}

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  workspace_id: string | null;
  /** May be omitted by some upstream detail payloads; UI must tolerate absence. */
  subscription?: PortalUserSubscriptionTier;
  is_guest: boolean;
  is_blocked: boolean;
  status: PortalUserStatus;
  monthly_used: number;
  monthly_limit: number;
  usage?: PortalSubscriptionUsage;
  creative_quota_override: number | null;
  disable_billing_conversion: boolean;
  hide_conversion_modal: boolean;
  pending_campaigns: number;
  created_at: string;
}

export interface PortalUsersPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalUser[];
}

export interface PortalUsageLimitConfig {
  monthly_creative_limit: number;
}

export interface PortalMetrics {
  new_users_30d: number;
  new_users_prior_30d: number;
  creatives_generated_30d: number;
  brands_ingested_30d: number;
  guest_signups_30d: number;
  guest_accounts_total: number;
  guest_accounts_unclaimed: number;
}

export interface PortalMetricsViewModel {
  newUsersLast30d: number;
  newUsersDelta: string;
  newUsersDeltaDirection: 'up' | 'down' | 'neutral';
  creativesGeneratedLast30d: number;
  brandsIngestedLast30d: number;
  guestSignupsLast30d: number;
  guestAccountsTotal: number;
  guestAccountsUnclaimed: number;
}

export type StatusFilter = 'all' | PortalUserStatus;
export type SubscriptionFilter = 'all' | PortalUserSubscriptionTier;

export interface ListPortalUsersParams {
  search?: string;
  status?: PortalUserStatus;
  subscription?: PortalUserSubscriptionTier;
  is_guest?: boolean;
  page?: number;
  page_size?: number;
}

export interface AdminSubscription {
  workspace_id: string;
  workspace_name: string | null;
  tier: PortalUserSubscriptionTier;
  billing_cycle: 'none' | 'monthly' | 'annual';
  is_portal_managed: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  topup_credit_balance: number;
  usage: PortalSubscriptionUsage;
  tier_options: string[];
}

export interface AdminSubscriptionListResponse {
  results: AdminSubscription[];
  page: number;
  page_size: number;
}

export interface ListAdminSubscriptionsParams {
  tier?: PortalUserSubscriptionTier;
  page?: number;
  page_size?: number;
}

export interface PortalActivityLogEntry {
  id: string;
  action: string;
  actor_email: string | null;
  created_at: string;
  summary: string | null;
}

export interface PortalActivityLogResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalActivityLogEntry[];
}

export interface PortalUserDetail extends PortalUser {
  workspace_name?: string | null;
  activity_log: PortalActivityLogResponse;
}

export interface PortalWorkspaceSummary {
  workspace_id: string;
  workspace_name: string;
  member_count: number;
  active_member_count?: number;
  blocked_member_count?: number;
  tier: PortalUserSubscriptionTier;
  monthly_used: number;
  monthly_limit: number;
  usage?: PortalSubscriptionUsage;
  pending_campaigns: number;
}

export interface PortalWorkspacesPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalWorkspaceSummary[];
}

export interface PortalWorkspaceMember {
  id: string;
  email: string;
  name: string;
  status: PortalUserStatus;
  subscription: PortalUserSubscriptionTier;
  monthly_used: number;
  monthly_limit: number;
  is_guest: boolean;
}

export interface PortalWorkspaceDetail {
  workspace_id: string;
  workspace_name: string;
  tier: PortalUserSubscriptionTier;
  member_count: number;
  monthly_used: number;
  monthly_limit: number;
  pending_campaigns: number;
  usage: PortalSubscriptionUsage;
  members: PortalWorkspaceMember[];
}

export interface PortalWorkspaceBulkSettings {
  creative_quota_override: number | null;
  disable_billing_conversion: boolean;
  hide_conversion_modal: boolean;
}

export interface ListPortalWorkspacesParams {
  search?: string;
  subscription?: PortalUserSubscriptionTier;
  page?: number;
  page_size?: number;
}

export interface GetPortalUserDetailParams {
  userId: string;
  page?: number;
  page_size?: number;
}

export interface UpdatePortalWorkspaceBulkSettingsParams {
  workspaceId: string;
  creative_quota_override?: number | null;
  disable_billing_conversion?: boolean;
  hide_conversion_modal?: boolean;
}

export const PORTAL_SYNC_NOT_CONFIGURED_ERROR_CODE = 'portal_sync_not_configured';

/** Tiers admins may set manually via the manage UI (workspace-scoped). */
export const ADMIN_SETTABLE_SUBSCRIPTION_TIERS = [
  'free',
  'basic',
  'pro',
  'media_partner',
  'internal',
] as const satisfies readonly PortalUserSubscriptionTier[];

export type AdminSettableSubscriptionTier = (typeof ADMIN_SETTABLE_SUBSCRIPTION_TIERS)[number];
